export type FibonacciBackoffOptions = {
  /** 首次等待毫秒，默认 5000ms */
  initialDelayMs?: number;
  /** 斐波那契最大步数，限定增长，默认 20 步 */
  maxSteps?: number;
  /** 每步的基准缩放系数，默认 1 */
  scale?: number;
  /** 抖动因子，0-1 之间，默认 0.2（±20% 随机抖动）*/
  jitter?: number;
  /** 最大等待上限，默认 10_000ms */
  maxDelayMs?: number;
};

/**
 * 生成“初次等待 + 斐波那契 + 抖动”的等待时间序列（毫秒）。
 * 用于轮询/重试等场景，避免雪崩与同步风暴。
 */
export function createFibonacciDelays(options?: FibonacciBackoffOptions): () => number {
  const {
    initialDelayMs = 5000,
    maxSteps = 20,
    scale = 1,
    jitter = 0.2,
    maxDelayMs = 10_000,
  } = options ?? {};

  // 斐波那契序列从 1, 1 开始
  let prev = 1;
  let curr = 1;
  let step = 0;
  let first = true;

  return () => {
    let baseDelay: number;
    if (first) {
      first = false;
      baseDelay = initialDelayMs;
    } else {
      // 控制最大步数，避免无限增长
      if (step < maxSteps) {
        const next = prev + curr;
        prev = curr;
        curr = next;
        step += 1;
      }
      // 斐波那契值乘以缩放系数
      baseDelay = curr * scale * 1000; // 基于 1000ms 的粒度，可通过 scale 调整
    }

    // 应用抖动：在 [1 - jitter, 1 + jitter] 区间内随机波动
    const jitterFactor = 1 + (Math.random() * 2 - 1) * Math.max(0, Math.min(1, jitter));
    const withJitter = Math.round(baseDelay * jitterFactor);
    return Math.min(withJitter, maxDelayMs);
  };
}

/** 简单 sleep */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 失败后重试若干次（默认额外重试 1 次，共最多执行 2 次）。
 * 每次重试前可选等待固定毫秒；onRetry 用于记录日志。
 */
export async function retryAsync<T>(
  fn: () => Promise<T>,
  options?: {
    /** 额外重试次数，默认 1（即首次失败后再试 1 次）*/
    retries?: number;
    /** 每次重试前等待毫秒，默认 0 */
    delayMs?: number;
    /** 每次重试前回调，attempt 从 1 开始计数 */
    onRetry?: (error: unknown, attempt: number) => void;
  },
): Promise<T> {
  const { retries = 1, delayMs = 0, onRetry } = options ?? {};
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      if (attempt < retries) {
        onRetry?.(e, attempt + 1);
        if (delayMs > 0) await sleep(delayMs);
      }
    }
  }
  throw lastError;
}

/**
 * 一个帮助函数：在用户提供的异步条件函数返回 truthy/完成时结束。
 * 每次检查前使用 Fibonacci + 抖动的等待时间。
 */
export async function pollWithFibonacciJitter<T>(
  check: () => Promise<{ done: boolean; value?: T }>,
  options?: FibonacciBackoffOptions & { timeoutMs?: number }
): Promise<T> {
  const { timeoutMs = 5 * 60 * 1000, ...rest } = options ?? {};
  const nextDelay = createFibonacciDelays(rest);
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    const result = await check();
    if (result.done) {
      return result.value as T;
    }

    await sleep(nextDelay());
  }

  throw new Error('Polling timed out');
}


